'use client';

import React, { useState, useRef } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import {
  UserIcon,
  BuildingOfficeIcon,
  DevicePhoneMobileIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

type AccountType = 'b2c_individual' | 'b2b_fleet';
type Step = 'details' | 'otp' | 'done';

export default function RegisterPage() {
  // Form state
  const [accountType, setAccountType] = useState<AccountType>('b2c_individual');
  const [fullName, setFullName] = useState('');
  const [mobile, setMobile] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Flow state
  const [step, setStep] = useState<Step>('details');
  const [otp, setOtp] = useState('');
  const [requestId, setRequestId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const cleanMobile = mobile.replace(/\D/g, '').slice(-10);

  // ── Step 1: Send OTP ──────────────────────────────────────────────────────
  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!fullName.trim()) { setError('Please enter your full name.'); return; }
    if (cleanMobile.length !== 10) { setError('Please enter a valid 10-digit mobile number.'); return; }
    if (accountType === 'b2b_fleet' && !companyName.trim()) { setError('Please enter your company name.'); return; }

    setLoading(true);
    try {
      const res = await fetch('/api/auth/send-otp-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: cleanMobile }),
      });
      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Failed to send OTP. Please try again.');
        return;
      }

      setRequestId(data.request_id || '');
      setStep('otp');
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: Verify OTP + Create Account ──────────────────────────────────
  const handleVerifyAndRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (otp.length !== 6) { setError('Please enter the complete 6-digit OTP.'); return; }

    setLoading(true);
    try {
      // 2a. Verify OTP
      const verifyRes = await fetch('/api/auth/verify-otp-register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: cleanMobile, otp, request_id: requestId }),
      });
      const verifyData = await verifyRes.json();

      if (!verifyRes.ok || !verifyData.success) {
        setError(verifyData.error || 'Invalid OTP. Please try again.');
        return;
      }

      // 2b. Create account
      const regRes = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mobile: cleanMobile,
          fullName: fullName.trim(),
          role: accountType,
          companyName: accountType === 'b2b_fleet' ? companyName.trim() : undefined,
        }),
      });
      const regData = await regRes.json();

      if (!regRes.ok) {
        setError(regData.error || 'Registration failed. Please try again.');
        return;
      }

      // Success — show done screen briefly then hard-redirect
      // The session cookie is already set by the register API
      setStep('done');
      const destination = regData.dashboard || (accountType === 'b2b_fleet' ? '/b2b-dashboard' : '/b2c-dashboard');
      setTimeout(() => {
        window.location.href = destination;
      }, 1500);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── OTP input helpers ─────────────────────────────────────────────────────
  const handleOtpChange = (val: string, idx: number) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    const arr = (otp + '      ').slice(0, 6).split('');
    arr[idx] = digit;
    const newOtp = arr.join('').trimEnd();
    setOtp(newOtp.slice(0, 6));
    if (digit && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === 'Backspace') {
      if (!otp[idx] && idx > 0) {
        otpRefs.current[idx - 1]?.focus();
      }
      const arr = (otp + '      ').slice(0, 6).split('');
      arr[idx] = '';
      setOtp(arr.join('').trimEnd().slice(0, 6));
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">

        {/* Logo */}
        <div className="text-center space-y-2">
          <Link href="/" className="inline-flex items-center gap-2.5 justify-center">
            <AppLogo size={40} />
            <span className="font-bold text-2xl text-foreground">ChallanDesk</span>
          </Link>
          <p className="text-muted-foreground text-sm">Create your free account</p>
        </div>

        {/* ── Done ── */}
        {step === 'done' && (
          <div className="card-base p-10 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircleIcon className="w-10 h-10 text-emerald-500" />
            </div>
            <div>
              <p className="text-xl font-bold text-foreground">Account Created!</p>
              <p className="text-sm text-muted-foreground mt-1">
                Welcome, {fullName}! Redirecting to your dashboard…
              </p>
            </div>
          </div>
        )}

        {/* ── OTP Step ── */}
        {step === 'otp' && (
          <div className="card-base p-8 space-y-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setStep('details'); setOtp(''); setError(''); }}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-lg font-bold text-foreground">Verify your number</h2>
                <p className="text-sm text-muted-foreground">OTP sent to +91 {cleanMobile}</p>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <form onSubmit={handleVerifyAndRegister} className="space-y-6">
              <div className="flex gap-2 justify-start">
                {Array.from({ length: 6 }).map((_, i) => (
                  <input
                    key={i}
                    ref={el => { otpRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={otp[i] || ''}
                    onChange={e => handleOtpChange(e.target.value, i)}
                    onKeyDown={e => handleOtpKeyDown(e, i)}
                    autoFocus={i === 0}
                    className="w-11 h-12 text-center text-lg font-bold border-2 border-border rounded-xl bg-background text-foreground focus:outline-none focus:border-accent transition-colors"
                  />
                ))}
              </div>

              <button
                type="submit"
                disabled={loading || otp.length !== 6}
                className="w-full py-3 bg-accent text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {loading ? 'Creating account…' : 'Verify & Create Account'}
              </button>
            </form>

            <button
              onClick={() => { setOtp(''); setError(''); setStep('details'); }}
              className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Change mobile number
            </button>
          </div>
        )}

        {/* ── Details Step ── */}
        {step === 'details' && (
          <>
            {/* Account type */}
            <div className="grid grid-cols-2 gap-3">
              {(['b2c_individual', 'b2b_fleet'] as AccountType[]).map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setAccountType(type)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                    accountType === type
                      ? 'border-accent bg-blue-50 text-accent' :'border-border bg-card text-muted-foreground hover:border-accent/50'
                  }`}
                >
                  {type === 'b2c_individual'
                    ? <UserIcon className="w-6 h-6" />
                    : <BuildingOfficeIcon className="w-6 h-6" />
                  }
                  <span className="text-sm font-semibold">
                    {type === 'b2c_individual' ? 'Individual' : 'Business / Fleet'}
                  </span>
                  <span className="text-xs opacity-70">
                    {type === 'b2c_individual' ? 'Personal vehicle' : 'Multiple vehicles'}
                  </span>
                </button>
              ))}
            </div>

            {/* Form */}
            <form onSubmit={handleSendOTP} className="card-base p-8 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}

              {/* Full Name */}
              <div className="space-y-1.5">
                <label htmlFor="fullName" className="block text-sm font-medium text-foreground">
                  Full Name *
                </label>
                <input
                  id="fullName"
                  type="text"
                  required
                  value={fullName}
                  onChange={e => setFullName(e.target.value)}
                  placeholder="Rahul Sharma"
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors text-sm"
                />
              </div>

              {/* Mobile */}
              <div className="space-y-1.5">
                <label htmlFor="mobile" className="block text-sm font-medium text-foreground">
                  Mobile Number *
                </label>
                <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-border bg-background focus-within:ring-2 focus-within:ring-accent/50 focus-within:border-accent transition-colors">
                  <DevicePhoneMobileIcon className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground text-sm font-semibold">+91</span>
                  <input
                    id="mobile"
                    type="tel"
                    required
                    value={mobile}
                    onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="98765 43210"
                    className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none text-sm"
                    maxLength={10}
                  />
                </div>
                <p className="text-xs text-muted-foreground">OTP will be sent to this number</p>
              </div>

              {/* Company Name (B2B only) */}
              {accountType === 'b2b_fleet' && (
                <div className="space-y-1.5">
                  <label htmlFor="companyName" className="block text-sm font-medium text-foreground">
                    Company Name *
                  </label>
                  <input
                    id="companyName"
                    type="text"
                    required
                    value={companyName}
                    onChange={e => setCompanyName(e.target.value)}
                    placeholder="Logistics India Pvt Ltd"
                    className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent transition-colors text-sm"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-accent text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
              >
                {loading && <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {loading ? 'Sending OTP…' : <><span>Send OTP</span><ArrowRightIcon className="w-4 h-4" /></>}
              </button>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link href="/login" className="text-accent font-semibold hover:underline">Sign in</Link>
              </p>
            </form>
          </>
        )}

        <p className="text-center text-xs text-muted-foreground">
          By signing up, you agree to our{' '}
          <Link href="#" className="hover:text-foreground underline">Terms of Service</Link>
          {' '}and{' '}
          <Link href="#" className="hover:text-foreground underline">Privacy Policy</Link>
        </p>

        <p className="text-center text-xs text-muted-foreground">
          <Link href="/" className="hover:text-foreground transition-colors">← Back to ChallanDesk</Link>
        </p>
      </div>
    </div>
  );
}
